import cx from 'classnames'
import { useState, useEffect } from 'react'
import moment from 'moment'
import dayjs, { Dayjs } from 'dayjs'
import { useDispatch } from 'react-redux'
import { useMachine } from '@xstate/react'
import { useMutation, useQuery } from 'react-query'

import Styles from './UpgradePlanModal.module.scss'

import { workspacesSet } from 'redux/actions'
import { upgradePlanMachine } from './upgradePlanMachine'
import { Workspace } from 'types'
import { useWorkspaces } from 'hooks/useWorkspaces'
import { SelectPlan } from './components/SelectPlan/SelectPlan'
import {
  ProvideReason,
  reasonPlaceholders,
  SelectedReason,
  stripeCancellationReasonForReason,
} from './components/ProvideReason/ProvideReason'
import { CancelSuccess, CancelSuccessFlowType } from './components/CancelSuccess/CancelSuccess'
import { SpecialOffer } from './components/SpecialOffer/SpecialOffer'
import { DiscountApplied } from './components/DiscountApplied/DiscountApplied'
import { PaymentForm } from './components/PaymentForm/PaymentForm'
import { WorkspaceClient, type PlanDetails } from 'api/clients/WorkspaceClient'
import { PaymentPlan } from 'api/clients/PaymentPlanClient'
import { FREE_PLAN, usePaymentPlans } from 'hooks/usePaymentPlans'
import { Modal } from 'almanac-ui/Modal'
import { useCurrentPlan } from 'hooks/useCurrentPlan'
import { useCurrentWorkspace } from 'hooks/useCurrentWorkspace'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { AutopilotPauseOption } from './components/AutopilotPauseOption/AutopilotPauseOption'
import { DeleteWorkspaceOption } from './components/DeleteWorkspaceOption/DeleteWorkspaceOption'
import { BeforeDeleteAccount } from './components/BeforeDeleteAccount/BeforeDeleteAccount'
import { DeleteAccountOption } from './components/DeleteAccountOption/DeleteAccountOption'
import { showToastMessage } from 'common/ToastMessage/ShowToastMessage'
import { useToggleAutopilot } from 'hooks/queries/workspaces/useToggleAutopilot'
import { useFlipperFlag } from 'hooks/useFlipperFlag'

export type BillingFrequency = 'year' | 'month'

interface Props {
  onClose: () => void
  onUpgradePlanSuccess?: (message?: string) => void
  onSuccess?: (paying: boolean, plan_details?: PlanDetails) => void
  showSuccessModal: boolean
  initialPage?: string
  initialSavedPlan?: PaymentPlan
  initialSelectedPlan?: PaymentPlan
  initialBillingFrequency?: BillingFrequency
  showWorkspacesLink?: boolean
  isCanceled?: boolean
  isDeactivatingWorkspace?: boolean
  onDeleteWorkspace?: () => void
  isDeletingAccount?: boolean
  onDeleteAccount?: () => void
  isSoleAdminOfMultiMemberWorkspace?: boolean
}

export const UpgradePlanModal = ({
  onClose,
  onUpgradePlanSuccess,
  onSuccess,
  showSuccessModal,
  initialSavedPlan,
  initialSelectedPlan,
  initialBillingFrequency,
  initialPage = 'selectPlan',
  showWorkspacesLink = false,
  isCanceled,
  isDeactivatingWorkspace = false,
  onDeleteWorkspace,
  isDeletingAccount = false,
  onDeleteAccount,
  isSoleAdminOfMultiMemberWorkspace = false,
}: Props) => {
  const [state, send] = useMachine(upgradePlanMachine({ initial: initialPage }))
  const { fetched: paymentPlansFetched, paymentPlans } = usePaymentPlans()
  const [billingFrequency, setBillingFrequency] = useState<BillingFrequency>(initialBillingFrequency || 'year')
  const [selectedPlan, setSelectedPlan] = useState<PaymentPlan | undefined>(initialSelectedPlan)
  const [savedPlan, setSavedPlan] = useState(initialSavedPlan || FREE_PLAN)
  const [subscriptionPeriodEndsAt, setSubscriptionPeriodEndsAt] = useState<Dayjs | null>(null)
  const [cancellationReasons, setCancellationReasons] = useState<SelectedReason[]>([])
  const [additionalFeedback, setAdditionalFeedback] = useState<string | undefined>(undefined)
  const dispatch = useDispatch()
  const workspaces = useWorkspaces()
  const workspace = useCurrentWorkspace()
  const [autopilotEndDate, setAutopilotEndDate] = useState<Dayjs | null>(
    workspace?.pause_autopilot_publish_at ? dayjs(workspace.pause_autopilot_publish_at) : null,
  )
  const workspaceId = useCurrentWorkspaceId()
  const { isFetched: currentPlanFetched, data: currentPlan } = useCurrentPlan()
  const isAutopilotUser = workspace?.is_virtual_marketer
  const toggleAutopilotMutation = useToggleAutopilot()
  const isNewChurnFlow = useFlipperFlag('new_churn_flow')

  // Check if user is eligible for the retention discount
  const { data: discountEligibilityData } = useQuery(
    ['cancelationDiscountEligibility', workspaceId],
    () => WorkspaceClient.subscriptionCancelationDiscountEligibility({ workspaceId }),
    { enabled: !!workspaceId },
  )
  const isDiscountEligible = (isAutopilotUser && discountEligibilityData?.data?.cancelation_discount_eligible) ?? false

  // Mutation for cancelling subscription
  const cancelSubscriptionMutation = useMutation(WorkspaceClient.cancelSubscription)

  // Mutation for applying retention discount
  const applyDiscountMutation = useMutation(WorkspaceClient.applyDeterCancelationCoupon)

  const handleCancelSubscription = async ({
    reasons,
    turnOffAutopilot = false,
    additionalFeedback,
  }: {
    reasons: SelectedReason[]
    turnOffAutopilot?: boolean
    additionalFeedback?: string
  }) => {
    const cancellationReason = stripeCancellationReasonForReason(reasons[0].reason)
    const reasonsDescription = reasons
      .map((sR) => {
        const subItems = sR.subItems ?? []
        const description = (sR.description || '').trim()
        const hasSubItems = subItems.length > 0
        const hasDescription = description.length > 0
        const placeholder = reasonPlaceholders[sR.reason]

        if (!hasSubItems && !hasDescription) return sR.reason
        if (hasSubItems && hasDescription) {
          const placeholderPart = placeholder ? `[${placeholder}] ` : ''
          return `${sR.reason}: ${subItems.join(', ')} ${placeholderPart}(Customer: ${description})`
        }
        if (hasSubItems) return `${sR.reason}: ${subItems.join(', ')}`
        const placeholderPart = placeholder ? `[${placeholder}] ` : ''
        return `${sR.reason} ${placeholderPart}(Customer: ${description})`
      })
      .join(' | ')

    const cancellationDescription = additionalFeedback
      ? `${reasonsDescription} | Additional Feedback: ${additionalFeedback}`
      : reasonsDescription

    // If user chose to turn off autopilot immediately, pause it first
    if (turnOffAutopilot && workspace) {
      const pauseAt = dayjs().toISOString()
      try {
        const updatedWorkspace = await toggleAutopilotMutation.mutateAsync({
          pauseAutopilotGenerateAt: pauseAt,
          pauseAutopilotPublishAt: pauseAt,
          resumeAutopilotGenerateAt: null,
          resumeAutopilotPublishAt: null,
        })
        setAutopilotEndDate(dayjs(updatedWorkspace.pause_autopilot_publish_at))
      } catch {
        showToastMessage("We couldn't turn off autopilot.", 'error')
      }
    }

    cancelSubscriptionMutation.mutate(
      {
        workspaceId,
        cancellationReason,
        cancellationDescription,
      },
      {
        onSuccess: (res) => {
          const periodEnd = dayjs.unix(res.data.subscription.cancel_at)
          setSubscriptionPeriodEndsAt(periodEnd)
          // Show appropriate option based on flow type and autopilot choice
          if (isDeletingAccount && turnOffAutopilot) {
            send('SHOW_DELETE_ACCOUNT_OPTION')
          } else if (isDeactivatingWorkspace && turnOffAutopilot) {
            send('SHOW_DELETE_WORKSPACE_OPTION')
          } else {
            send('CANCEL_SUCCESS')
          }
        },
        onError: () => {
          showToastMessage('Unable to cancel your subscription', 'error')
        },
      },
    )
  }

  const handleAcceptOffer = () => {
    applyDiscountMutation.mutate(
      { workspaceId },
      {
        onSuccess: (response) => {
          if (!response.data.subscription.discount) {
            showToastMessage('Unable to apply discount', 'error')
            return
          }
          send('ACCEPT_OFFER')
        },
        onError: () => {
          showToastMessage('Unable to apply discount', 'error')
        },
      },
    )
  }

  const updateWorkspace = (updateFields: any) => {
    const updatedWorkspaces = workspaces.map((ws) => {
      if (ws.id === workspace?.id) {
        return { ...workspace, ...updateFields } as Workspace
      }

      return ws
    })
    dispatch(workspacesSet(updatedWorkspaces))
  }

  const updateWorkspacePaying = (paying: boolean) => {
    if (!paying) return

    updateWorkspace({ paying })
  }

  const startDate = () => {
    return moment().format('ll')
  }

  const selectPlan = (plan: PaymentPlan) => {
    setSelectedPlan(plan)
    if (plan === FREE_PLAN) {
      send('PROVIDE_REASON')
    } else {
      send('CONTINUE_TO_PAYMENT')
    }
  }

  useEffect(() => {
    if (paymentPlansFetched && !selectedPlan) {
      setSelectedPlan(paymentPlans[0])
    }
  }, [setSelectedPlan, paymentPlansFetched, selectedPlan, paymentPlans])

  useEffect(() => {
    if (currentPlanFetched && paymentPlansFetched) {
      const plan = paymentPlans.find((p) => p.key === currentPlan?.plan)
      plan && setSavedPlan(plan)
    }
  }, [currentPlanFetched, paymentPlansFetched, currentPlan, paymentPlans])

  useEffect(() => {
    if (state.matches('paymentSuccess')) {
      if (showSuccessModal) {
        send('CONTINUE')
      } else {
        onClose()
      }
    }
  }, [state, showSuccessModal, onClose, send])

  if (!workspace) return null

  const isCancelFlow =
    state.matches('beforeDeleteAccount') ||
    state.matches('provideReason') ||
    state.matches('specialOffer') ||
    state.matches('autopilotPauseOption') ||
    state.matches('deleteWorkspaceOption') ||
    state.matches('deleteAccountOption') ||
    state.matches('discountApplied') ||
    state.matches('cancelSuccess')

  // Get subscription end date for AutopilotPauseOption display
  const subscriptionEndDate = currentPlan?.next_payment?.due_date
    ? dayjs(currentPlan.next_payment.due_date)
    : dayjs().add(1, 'month')

  const handleContinueCancelling = () => {
    if (isAutopilotUser) {
      send('SHOW_AUTOPILOT_PAUSE_OPTION')
    } else {
      handleCancelSubscription({ reasons: cancellationReasons })
    }
  }

  const getFlowType = (): CancelSuccessFlowType => {
    if (isDeletingAccount) {
      return 'deleteAccount'
    }
    if (isDeactivatingWorkspace) {
      return 'deleteWorkspace'
    }
    return 'cancelSubscription'
  }

  if (state.matches('success')) {
    let message = "Success — you've upgraded your plan!"
    if (selectedPlan?.name) {
      message = `Success — you're now on the ${selectedPlan.name} plan! `
    }
    if (onUpgradePlanSuccess) {
      onUpgradePlanSuccess(message)
    } else {
      onClose()
    }
    return null
  } else {
    return (
      <Modal.Root
        onPressOutside={onClose}
        onClose={onClose}
        size="md"
        bodyClassName={cx({
          [Styles.cancelFlow]: isCancelFlow,
          [Styles.autopilotPauseOption]: state.matches('autopilotPauseOption'),
          [Styles.deleteWorkspaceOption]: state.matches('deleteWorkspaceOption'),
          [Styles.beforeDeleteAccount]: state.matches('beforeDeleteAccount'),
          [Styles.deleteAccountOption]: state.matches('deleteAccountOption'),
          [Styles.specialOffer]: state.matches('specialOffer'),
        })}
      >
        {state.matches('selectPlan') && (
          <SelectPlan
            availablePlans={paymentPlans}
            billingFrequency={billingFrequency}
            onClose={onClose}
            savedPlan={savedPlan}
            selectPlan={selectPlan}
            setBillingFrequency={setBillingFrequency}
            showWorkspacesLink={showWorkspacesLink}
            isCanceled={isCanceled}
          />
        )}
        {state.matches('beforeDeleteAccount') && (
          <BeforeDeleteAccount
            onBack={onClose}
            onDeleteAccount={() => send('SHOW_PROVIDE_REASON')}
            onClose={onClose}
            isSoleAdminOfMultiMemberWorkspace={isSoleAdminOfMultiMemberWorkspace}
          />
        )}
        {state.matches('provideReason') && (
          <ProvideReason
            onCancel={onClose}
            initialSelectedReasons={cancellationReasons}
            initialAdditionalFeedback={additionalFeedback}
            onNext={(selectedReasons, additionalFeedback) => {
              setCancellationReasons(selectedReasons)
              setAdditionalFeedback(additionalFeedback)

              if (isNewChurnFlow) {
                // New flow: show special offer or autopilot pause option before cancelling
                if (isDiscountEligible) {
                  send('SHOW_SPECIAL_OFFER')
                } else if (isAutopilotUser) {
                  send('SHOW_AUTOPILOT_PAUSE_OPTION')
                } else {
                  handleCancelSubscription({ reasons: selectedReasons, additionalFeedback })
                }
              } else {
                // Old flow: cancel subscription directly
                handleCancelSubscription({ reasons: selectedReasons, additionalFeedback })
              }
            }}
          />
        )}
        {state.matches('specialOffer') && (
          <SpecialOffer
            onBack={() => send('SHOW_PROVIDE_REASON')}
            onContinueCancelling={handleContinueCancelling}
            onAcceptOffer={handleAcceptOffer}
            onClose={onClose}
            isLoading={cancelSubscriptionMutation.isLoading || applyDiscountMutation.isLoading}
          />
        )}
        {state.matches('autopilotPauseOption') && (
          <AutopilotPauseOption
            onBack={() => send(isDiscountEligible ? 'SHOW_SPECIAL_OFFER' : 'SHOW_PROVIDE_REASON')}
            onCancelSubscription={(turnOffAutopilot) =>
              handleCancelSubscription({ reasons: cancellationReasons, turnOffAutopilot, additionalFeedback })
            }
            onClose={onClose}
            subscriptionEndDate={subscriptionEndDate}
            isLoading={cancelSubscriptionMutation.isLoading}
          />
        )}
        {state.matches('deleteWorkspaceOption') && (
          <DeleteWorkspaceOption
            onBack={() => send('SHOW_AUTOPILOT_PAUSE_OPTION')}
            onDeleteWorkspace={() => {
              onDeleteWorkspace?.()
              onClose()
            }}
            onKeepAccount={onClose}
            onClose={onClose}
          />
        )}
        {state.matches('deleteAccountOption') && (
          <DeleteAccountOption
            onBack={() => send('SHOW_AUTOPILOT_PAUSE_OPTION')}
            onDeleteAccount={() => {
              onDeleteAccount?.()
              onClose()
            }}
            onKeepAccount={onClose}
            onClose={onClose}
          />
        )}
        {state.matches('discountApplied') && <DiscountApplied onClose={onClose} />}
        {state.matches('cancelSuccess') && subscriptionPeriodEndsAt && (
          <CancelSuccess
            onClose={onClose}
            onBack={() => send('SHOW_AUTOPILOT_PAUSE_OPTION')}
            subscriptionEndDate={autopilotEndDate ?? subscriptionPeriodEndsAt}
            isAutopilotUser={!!isAutopilotUser}
            flowType={getFlowType()}
          />
        )}
        {(state.matches('payment') || state.matches('paymentSuccess')) && selectedPlan && (
          <PaymentForm
            onClose={onClose}
            onSuccess={onSuccess || updateWorkspacePaying}
            selectedPlan={selectedPlan}
            savedPlan={savedPlan}
            billingFrequency={billingFrequency}
            setBillingFrequency={setBillingFrequency}
            workspace={workspace}
            send={send}
            startDate={startDate()}
            isCanceled={isCanceled}
          />
        )}
      </Modal.Root>
    )
  }
}
